/**
 * Store Pinia des indexes (Composition API)
 */
import { defineStore } from "pinia";
import { ref } from "vue";

import { IIndex } from "../models/index";
import { IIndexApiResponse } from "../models/api/indexApiResponse";
import { IIndexForm } from "../models/forms/indexForm";
import { IIndexFilter } from "../models/filters/indexFilter";
import IndexServices from "../services/IndexService";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IAnimal } from "../models/animals";
import { useBoars } from "./boars.store";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";

/**
 * Store Pinia des indexes utilisant la Composition API
 */
export const useIndexStore = defineStore("indexes", () => {
    // State
    const datas = ref<IIndex[]>([]);
    const current = ref<IIndex>({} as IIndex);
    const currentTest = ref<IIndex>({} as IIndex);
    const currentIFIP = ref<IIndex>({} as IIndex);
    const itemForm = ref<IIndexForm>({} as IIndexForm);
    const itemFormTest = ref<IIndexForm>({} as IIndexForm);
    const itemFormIFIP = ref<IIndexForm>({} as IIndexForm);
    const indexCount = ref<number>(0);
    const loadedCenterId = ref<number | undefined>(undefined);

    // Actions - Hydration

    /**
     * Hydrate Indexes from API response
     * Links animals by ID using boarsStore (IBoar extends IAnimal).
     * boarsStore is pre-populated by hydrateProductions before indexStore.load() runs.
     */
    async function hydrateIndexes(indexes: IIndexApiResponse[]): Promise<IIndex[]> {
        if (indexes.length === 0) return [];

        // Utiliser boarsStore (IBoar extends IAnimal) — déjà chargé par hydrateProductions.
        // Ne pas forcer de chargement ici pour éviter un appel réseau sans filtre centre.
        const boarsStore = useBoars();
        const animalMap = new Map(boarsStore.datas.map(a => [a.id, a]));

        const models: IIndex[] = [];
        for (const idx of indexes) {
            const model = { ...idx } as unknown as IIndex;
            model.animal = animalMap.get(idx.animalId) ?? model.animal;
            models.push(model);
        }

        return models;
    }

    // Actions

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: IIndexFilter) {
        const result = await IndexServices.getAll<IIndexFilter, IIndexApiResponse>(filter);
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateIndexes(result.data as IIndexApiResponse[]);
        }
        loadedCenterId.value = filter.centerId;
        return result;
    }

    /**
     * Récupération d'un index par son identifiant
     * @param id
     */
    async function get(id: number) {
        const result = await IndexServices.getById<IIndexApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateIndexes([result.data as IIndexApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0) {
                datas.value[index] = modelData;
            }

            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    /**
     * Récupération du dernier index de l'animal
     * @param idAnimal Animal dont on souhaite l'index
     * @param typeIndex Type Index
     * @returns Dernier index créé
     */
    async function getLastIndexAnimal(idAnimal: number, typeIndex: string) {
        const index = await IndexServices.getLastIndexAnimal(idAnimal, typeIndex);
        console.log("Dernier index récupéré : ", index);

        if (!index.error) {
            if (index.data.vgQualiteMat == 0) {
                index.data.vgQualiteMat = "" as any;
            }

            if (index.data.vgProd == 0) {
                index.data.vgProd = "" as any;
            }

            if (index.data.vgGlobale == 0) {
                index.data.vgGlobale = "" as any;
            }

            if (typeIndex == "TESTAGE") {
                currentTest.value = index.data;
                itemFormTest.value = toForm(index.data as IIndex);
            } else {
                currentIFIP.value = index.data;
                itemFormIFIP.value = toForm(index.data as IIndex);
            }

            return index.data;
        }

        if (typeIndex == "TESTAGE") {
            resetItemFormTestValues();
        } else {
            resetItemFormIFIPValues();
        }

        return null;
    }

    async function getIndexCountForAnimal(animalId: number) {
        const count = await IndexServices.getIndexCountForAnimal(animalId);

        if (!count.error) {
            indexCount.value = count.data;
            return count.data;
        }
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IIndexApiResponse | IDataOperationResult<IIndexApiResponse>>;

        if (itemForm.value.id > 0) {
            result = await IndexServices.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateIndexes([result.data as IIndexApiResponse]);
                current.value = modelData;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IIndex>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await IndexServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateIndexes([result.data as IIndexApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    function toForm(index: IIndex): IIndexForm {
        const form = {} as IIndexForm;
        Object.assign(form, index);
        // Use animalId property directly instead of animal.id
        form.animalId = index.animalId;

        return form;
    }

    function newForm(lienId: number): IIndexForm {
        itemForm.value = {
            id: 0,
            animalId: lienId,
            date: new Date(),
            evaluationDate: new Date(),
            origin: "TESTAGE",
        } as IIndexForm;
        return itemForm.value;
    }

    function newItem(lien: IAnimal): IIndex {
        current.value = {
            animalId: lien.id,
            animal: lien,
            date: new Date(),
            evaluationDate: new Date(),
        } as IIndex;
        return current.value;
    }

    function resetItemFormTestValues() {
        itemFormTest.value = {
            id: 0,
            animalId: undefined,
            date: undefined,
            evaluationDate: undefined,
            origin: undefined,
            vgQualiteMat: 0,
            vgProd: 0,
            vgGlobale: 0
        } as IIndexForm;
    }

    function resetItemFormIFIPValues() {
        itemFormIFIP.value = {
            id: 0,
            animalId: undefined,
            date: undefined,
            evaluationDate: undefined,
            origin: undefined,
            vgQualiteMat: 0,
            vgProd: 0,
            vgGlobale: 0
        } as IIndexForm;
    }

    return {
        // State
        datas,
        current,
        currentTest,
        currentIFIP,
        itemForm,
        itemFormTest,
        itemFormIFIP,
        indexCount,
        loadedCenterId,
        // Actions
        hydrateIndexes,
        load,
        get,
        searchStoreById,
        getLastIndexAnimal,
        getIndexCountForAnimal,
        save,
        toForm,
        newForm,
        newItem,
        resetItemFormTestValues,
        resetItemFormIFIPValues
    };
});

signalRManager.connection.on("MajIndex", async (payload: IPayloadStore) => {
    const store = useIndexStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateIndexes([payload.data as IIndexApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateIndexes([payload.data as IIndexApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
